import moment from "moment";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Label,
  Pagination,
  Select,
  Spinner,
  Table,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery } from "react-query";
import { CVS, SURVEYS, USERS } from "../../config/apiConfig";
import { formatToReal } from "../../utils/utils";
import { IoMdSearch } from "react-icons/io";
import EmptyTable from "./EmptyTable";
import { MdOutlineAttachMoney } from "react-icons/md";
import { BsFillBarChartFill } from "react-icons/bs";
import DrawerLoading from "../DrawerLoading";
import { IoCloudDownload } from "react-icons/io5";
import TableLoading from "../TableLoading";
import { useApplication } from "../../contexts/ApplicationContext";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { LuFilter, LuFilterX } from "react-icons/lu";

const ReportCvsTable = () => {
  const { user } = useApplication();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalPriceSelledSum, setTotalPriceSelledSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => setCurrentPage(page);
  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const [selectedQueries, setSelectedQueries] = useState([]);
  const [selectedUser, setSelectedUser] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);

  useEffect(() => {
    if (showFilters) {
      setRenderFilters(true); // mostra o conteúdo imediatamente
    } else {
      // espera a animação terminar antes de esconder
      const timeout = setTimeout(() => setRenderFilters(false), 500); // mesmo tempo da transition
      return () => clearTimeout(timeout);
    }
  }, [showFilters]);

  const {
    isLoading: isLoadingUsers,
    error: errorUsers,
    data: users,
  } = useQuery(["users"], async () => {
    const { data: items } = await axios.get(
      `${USERS.GET_USERS}?itemsPerPage=-1`
    );

    return items.data.data;
  });

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const {
    isLoading: isLoadingReportSurvey,
    error: errorListReportSurvey,
    data: listReportCvs,
  } = useQuery(
    [
      "listReportCvs",
      startDate,
      endDate,
      currentPage,
      selectedQueries,
      selectedStatuses,
      nameParam,
      selectedUser,
    ],
    async () => {
      const { data } = await axios.get(
        `${CVS.GET_REPORT}?startDate=${startDate}&endDate=${endDate}&page=${currentPage}&status=${selectedStatuses}&search=${nameParam}&userId=${selectedUser}`
      );

      setTotalPages(data.data.totalPages);
      setTotalPriceSum(data.data.totalPriceSum);
      setTotalCount(data.data.totalCount);

      return data.data.data;
    }
  );

  const handleCheckboxChange = (status, setState) => {
    setState((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName);
  };

  const handlePrint = async () => {
    try {
      const response = await axios.get(
        `${CVS.EXPORT_CVS}?startDate=${startDate}&endDate=${endDate}&status=${selectedStatuses}&search=${nameParam}&userId=${selectedUser}`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "historico_cvs.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Erro ao exportar relatório:", error);
      alert("Erro ao exportar relatório.");
    }
  };

  return (
    <>
      {user?.Company?.hasCV ? (
        <div className="relative">
          <div className="flex justify-between items-center mb-2">
            <Button
              size="xs"
              onClick={() => setShowFilters((prev) => !prev)}
              className="primary text-white transition-all duration-300"
            >
              <div className="flex items-center gap-2 transition-all duration-300">
                <span>
                  {showFilters ? "Esconder filtros" : "Mostrar filtros"}
                </span>
                <span className={`transition-transform duration-300 `}>
                  {!showFilters ? <LuFilter /> : <LuFilterX />}
                </span>
              </div>
            </Button>
          </div>
          <div className="flex flex-col gap-4">
            {showFilters && (
              <div
                className={`transition-all duration-500 ease-in-out overflow-hidden bg-white rounded-md shadow-md p-4 relative`}
              >
                {renderFilters && (
                  <div className="transition-opacity duration-500 min-w-[200px]">
                    <div className="title-syncx">Filtro avançado</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-x-10 md:gap-y-4">
                      <div className="mt-2">
                        <div className="font-semibold mt-4 text-sm mb-2">
                          Usuários
                        </div>
                        <Select
                          label="Usuário"
                          name="userId"
                          value={selectedUser}
                          onChange={(e) => setSelectedUser(e.target.value)}
                        >
                          <option value="">Todos os usuários</option>
                          {users?.map((user, idx) => (
                            <option key={idx} value={user?.id}>
                              {user?.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <div className="font-semibold mt-6 text-sm mb-3">
                          Período
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => {
                              setStartDate(e.target.value);
                              if (
                                moment(e.target.value).isAfter(moment(endDate))
                              ) {
                                setEndDate(e.target.value);
                              }
                            }}
                            className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                          />
                          <div className="text-xs">Até</div>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => {
                              setEndDate(e.target.value);
                            }}
                            min={startDate}
                            className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                          />
                        </div>
                      </div>
                      <div className="text-sm">
                        <div className="font-semibold">Status</div>
                        <ul className="mt-2 flex flex-col md:flex-row gap-2 md:gap-4 flex-wrap">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="checkbox-success"
                              checked={selectedStatuses.includes(2)}
                              onChange={() =>
                                handleCheckboxChange(2, setSelectedStatuses)
                              }
                            />
                            <Label htmlFor="checkbox-success" className="flex">
                              Enviado
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="checkbox-error"
                              checked={selectedStatuses.includes(1)}
                              onChange={() =>
                                handleCheckboxChange(1, setSelectedStatuses)
                              }
                            />
                            <Label htmlFor="checkbox-error" className="flex">
                              Erro
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="checkbox-progress"
                              checked={selectedStatuses.includes(0)}
                              onChange={() =>
                                handleCheckboxChange(0, setSelectedStatuses)
                              }
                            />
                            <Label htmlFor="checkbox-progress" className="flex">
                              Em progresso
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="checkbox-cancel"
                              checked={selectedStatuses.includes(-1)} // 0 = progress
                              onChange={() =>
                                handleCheckboxChange(-1, setSelectedStatuses)
                              }
                            />
                            <Label htmlFor="checkbox-cancel" className="flex">
                              Cancelado
                            </Label>
                          </div>
                        </ul>
                      </div>
                    </div>
                    {isLoadingUsers && <DrawerLoading />}
                  </div>
                )}
              </div>
            )}

            <div className="flex-1">
              <div className="title-syncx mt-3 mb-4">Resultado da busca</div>
              {startDate && endDate && (
                <div className="italic text-sm mb-2 flex items-center gap-2">
                  Informações baseadas no período de{" "}
                  <Badge className="w-fit">
                    {moment(startDate).format("DD/MM/yyyy")}
                  </Badge>{" "}
                  até{" "}
                  <Badge className="w-fit">
                    {" "}
                    {moment(endDate).format("DD/MM/yyyy")}
                  </Badge>
                </div>
              )}

              <div className="overflow-x-scroll">
                <div className="flex gap-3 pb-3" style={{ minWidth: "500px" }}>
                  <Card
                    className="w-full bg-gray-700 relative"
                    style={{ minWidth: "150px" }}
                  >
                    <div className="text-sm md:text-md lg:text-lg text-white">
                      Valor Total:
                    </div>
                    <div className="text-2xl text-white font-semibold flex gap-2 items-center">
                      {!isLoadingReportSurvey ? (
                        `${formatToReal(totalPriceSum)}`
                      ) : (
                        <div
                          role="status"
                          className="max-w-sm animate-pulse inline"
                        >
                          <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                        </div>
                      )}
                    </div>
                    <MdOutlineAttachMoney className="absolute bottom-0 right-0 text-white text-9xl opacity-10" />
                  </Card>
                  <Card className="w-full bg-gray-700 relative overflow-hidden">
                    <div className="text-sm md:text-md lg:text-lg text-white">
                      Total de comunicados:
                    </div>
                    <div className="text-2xl text-white font-semibold">
                      {!isLoadingReportSurvey ? (
                        `${totalCount}`
                      ) : (
                        <div
                          role="status"
                          className="max-w-sm animate-pulse inline"
                        >
                          <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                        </div>
                      )}
                    </div>
                    <BsFillBarChartFill className="absolute bottom-0 right-2 text-white text-9xl opacity-10" />
                  </Card>
                </div>
              </div>
              <div className="flex justify-end my-2">
                <Button onClick={handlePrint}>Imprimir</Button>
              </div>
              <form
                className="flex gap-1 mt-0 w-full pl-3 pr-2 py-2 md:py-1 rounded-md shadow-sm bg-gray-300"
                onSubmit={handleSearchName}
              >
                <input
                  type="text"
                  placeholder="Busque pelo nome do vendedor ou comprador..."
                  className="border-0 py-1 text-sm mt-1 focus:outline-none focus:ring-0 focus:border-gray-300 px-0 w-full bg-gray-300 placeholder:text-gray-700"
                  onChange={(e) => setSearchName(e.target.value)}
                  value={searchName}
                />
                <Button
                  size="sm"
                  className="flex items-center bg-secondary w-20"
                  type="submit"
                >
                  <IoMdSearch />
                </Button>
              </form>

              <small
                className="gap-2 items-center mt-5 mb-1 flex md:hidden justify-center"
                style={{ fontSize: 10 }}
              >
                <span>
                  Deslize para o lado para visualizar todas as informações
                </span>{" "}
                <FaArrowsLeftRight />
              </small>
              <>
                {listReportCvs?.length > 0 || isLoadingReportSurvey ? (
                  <div className="overflow-x-auto shadow-md rounded-lg mt-0 md:mt-5">
                    <Table striped style={{ fontSize: "13px" }}>
                      <Table.Head>
                        <Table.HeadCell className="text-center bg-gray-300 p-2">
                          Protocolo
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300 p-2">
                          Data cadastro
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300 p-2">
                          Placa
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300 p-2">
                          Nome Comprador
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300 p-2">
                          Nome Vendedor
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300 p-2">
                          Usuário
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300 p-2">
                          Situação
                        </Table.HeadCell>
                      </Table.Head>

                      <Table.Body className="divide-y">
                        {!isLoadingReportSurvey ? (
                          <>
                            {listReportCvs?.length > 0 && (
                              <>
                                {listReportCvs?.map((cv, index) => (
                                  <Table.Row
                                    key={index}
                                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                                  >
                                    <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                      {cv.protocolo || "-"}
                                    </Table.Cell>
                                    <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                      {moment(cv.createdAt).format(
                                        "DD/MM/yyyy"
                                      )}
                                    </Table.Cell>
                                    <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                      {cv.placa}
                                    </Table.Cell>
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white text-center p-2">
                                      {cv.nomeComprador}
                                    </Table.Cell>
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white text-center p-2">
                                      {cv.nomeVendedor}
                                    </Table.Cell>
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white text-center p-2">
                                      {cv?.User?.name && (
                                        <Badge
                                          className="w-fit mx-auto"
                                          color="gray"
                                        >
                                          {cv.User.name.split(" ")[0]}
                                        </Badge>
                                      )}
                                    </Table.Cell>
                                    <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                      {cv.status == "-1" && (
                                        <Badge
                                          className="w-fit mx-auto"
                                          color="failure"
                                        >
                                          Cancelado
                                        </Badge>
                                      )}
                                      {cv.status == "0" && (
                                        <Badge
                                          className="w-fit mx-auto"
                                          color="gray"
                                        >
                                          Em Progresso
                                        </Badge>
                                      )}
                                      {cv.status == "1" && (
                                        <Badge
                                          className="w-fit mx-auto"
                                          color="failure"
                                        >
                                          Erro
                                        </Badge>
                                      )}
                                      {cv.status == "2" && (
                                        <Badge
                                          className="w-fit mx-auto"
                                          color="success"
                                        >
                                          Enviado
                                        </Badge>
                                      )}
                                    </Table.Cell>
                                  </Table.Row>
                                ))}
                              </>
                            )}
                          </>
                        ) : (
                          <TableLoading cols={7} />
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <EmptyTable icon="search_off">
                    Nenhum registro encontrado com esses filtros
                  </EmptyTable>
                )}
              </>

              {totalPages > 1 && (
                <div className="flex overflow-x-auto justify-center mt-3">
                  <Pagination
                    layout="pagination"
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    previousLabel=""
                    nextLabel=""
                    showIcons
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <ForbiddenQueriesPermission />
      )}
    </>
  );
};

export default ReportCvsTable;
